import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import GridOverlay from '../components/GridOverlay';
import Brand from '../components/Brand';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { apiChangePassword } from '../lib/api';

// Mirror of backend policy for inline feedback. Server is authoritative.
const RULES = [
    { label: 'At least 12 characters', test: (v) => v.length >= 12 },
    { label: 'An uppercase letter (A-Z)', test: (v) => /[A-Z]/.test(v) },
    { label: 'A lowercase letter (a-z)', test: (v) => /[a-z]/.test(v) },
    { label: 'A digit (0-9)', test: (v) => /\d/.test(v) },
];

export default function ChangePassword() {
    const { user, mustChangePassword, clearForcedRotation } = useAuth();
    const navigate = useNavigate();
    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const policyPassing = RULES.every((r) => r.test(newPw));
    const matches = newPw.length > 0 && newPw === confirmPw;
    const canSubmit = policyPassing && matches && currentPw.length > 0 && !submitting;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setError('');
        try {
            const resp = await apiChangePassword(currentPw, newPw);
            clearForcedRotation(resp?.csrf_token);
            setSuccess(true);
            setTimeout(() => navigate('/admin', { replace: true }), 900);
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 401) {
                setError('Current password is incorrect.');
            } else if (status === 400 && detail?.errors) {
                setError(`Password does not meet policy: ${detail.errors.join('; ')}`);
            } else if (status === 400 && typeof detail === 'string') {
                setError(detail);
            } else {
                setError('Could not change password. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <SEO title="Change Password" description="Change your Centurion admin password." path="/admin/change-password" noindex />
            <section
                className="relative flex min-h-[80vh] items-center justify-center bg-brand-base px-6 py-16"
                data-testid="change-password-page"
            >
                <GridOverlay size={56} opacity={0.16} />
                <div
                    className="relative z-10 w-full max-w-md border border-brand-hairline bg-brand-surface p-8 md:p-10"
                    data-testid="change-password-card"
                >
                    <div className="flex justify-center">
                        <Brand height={72} interactive={false} asLink={false} testId="change-password-brand" />
                    </div>

                    <span className="mt-8 block text-center font-display text-[10px] font-bold uppercase tracking-[0.24em] text-brand-gold">
                        {mustChangePassword ? 'Required Rotation' : 'Update Password'}
                    </span>
                    <h1 className="mt-3 text-center font-display text-2xl font-bold uppercase tracking-tight text-brand-text">
                        {mustChangePassword ? 'Set a new password' : 'Change password'}
                    </h1>
                    <p className="mt-3 text-center font-sans text-sm text-brand-muted">
                        {mustChangePassword
                            ? `Signed in as ${user?.email}. Choose a new password to continue.`
                            : 'Enter your current password and a new one that meets the policy below.'}
                    </p>

                    <form onSubmit={onSubmit} className="mt-8 grid gap-5" data-testid="change-password-form" noValidate>
                        <PasswordField label="Current password" value={currentPw} onChange={setCurrentPw} testId="change-password-current" autoComplete="current-password" />
                        <PasswordField label="New password" value={newPw} onChange={setNewPw} testId="change-password-new" autoComplete="new-password" />
                        <PasswordField label="Confirm new password" value={confirmPw} onChange={setConfirmPw} testId="change-password-confirm" autoComplete="new-password" />

                        <ul className="mt-1 grid gap-1 border border-brand-hairline bg-brand-elevated px-4 py-3" data-testid="change-password-policy">
                            {RULES.map((r) => (
                                <li key={r.label} className="flex items-center gap-2 font-sans text-xs text-brand-muted">
                                    <CheckCircle2 size={12} className={r.test(newPw) ? 'text-brand-success' : 'text-brand-hairline'} />
                                    {r.label}
                                </li>
                            ))}
                            <li className="flex items-center gap-2 font-sans text-xs text-brand-muted">
                                <CheckCircle2 size={12} className={matches ? 'text-brand-success' : 'text-brand-hairline'} />
                                New and confirm match
                            </li>
                        </ul>

                        {error ? (
                            <p className="flex items-start gap-2 font-sans text-sm text-brand-danger" data-testid="change-password-error">
                                <AlertCircle size={14} className="mt-0.5 flex-none" /> {error}
                            </p>
                        ) : null}
                        {success ? (
                            <p className="flex items-start gap-2 font-sans text-sm text-brand-success" data-testid="change-password-success">
                                <CheckCircle2 size={14} className="mt-0.5 flex-none" /> Password updated. Redirecting to admin…
                            </p>
                        ) : null}

                        <button
                            type="submit"
                            disabled={!canSubmit}
                            data-testid="change-password-submit"
                            className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-gold px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-[#E5B647] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {submitting ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : 'Set new password'}
                        </button>
                    </form>
                </div>
            </section>
        </>
    );
}

const PasswordField = ({ label, value, onChange, testId, autoComplete }) => (
    <label className="block">
        <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">
            {label}
        </span>
        <input
            type="password"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            autoComplete={autoComplete}
            required
            data-testid={testId}
            className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
        />
    </label>
);
